// Adapter para búsqueda global - Global search adapter
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import type {
  GlobalSearchApiItem,
  GlobalSearchApiResponse,
  GlobalSearchResponse,
  GlobalSearchResult,
} from '../models/GlobalSearch';

export class GlobalSearchAdapter {
  constructor(private httpClient: ReturnType<typeof createHttpClient>) {}

  async search(query: string, page: number = 1, perPage: number = 15): Promise<GlobalSearchResponse> {
    if (!query || query.trim().length === 0) {
      return {
        items: [],
        total: 0,
        currentPage: 1,
        lastPage: 1,
        hasNextPage: false,
        nextPage: null,
      };
    }

    try {
      const params = { 
        keyword: query,
        page,
        per_page: perPage,
      };
      const response = await this.httpClient.get<GlobalSearchApiResponse>(
        '/global-search',
        params,
        {},
        false
      );

      const data = Array.isArray(response?.data) ? response.data : [];
      const items = data.map((item) => this.mapItemToResult(item));

      const currentPage = typeof response?.current_page === 'number' ? response.current_page : page;
      const lastPage = typeof response?.last_page === 'number' ? response.last_page : 1;
      const total = typeof response?.total === 'number' ? response.total : items.length;
      const hasNextPage = currentPage < lastPage;
      const nextPage = hasNextPage ? currentPage + 1 : null;

      return {
        items,
        total,
        currentPage,
        lastPage,
        hasNextPage,
        nextPage,
      };
    } catch (error) {
      console.log('🔍 GlobalSearchAdapter - Error:', error);
      return {
        items: [],
        total: 0,
        currentPage: 1,
        lastPage: 1,
        hasNextPage: false,
        nextPage: null,
      };
    }
  }

  // Adaptar item de API al modelo de resultados - Adapt API item to view model
  private mapItemToResult(item: GlobalSearchApiItem): GlobalSearchResult {
    const parsedUrl = this.getPathFromRoute(item.route);

    switch (item.type) {
      case 'product':
        return {
          id: item.id,
          type: item.type,
          title: item.name || `Producto ${item.id}`,
          subtitle: item.code,
          code: item.code,
          imageUrl: item.img_url,
          url: parsedUrl,
        };
      case 'order':
        return {
          id: item.id,
          type: item.type,
          title: item.ref || `Orden ${item.id}`,
          subtitle: item.store_name,
          storeName: item.store_name,
          statusName: item.status_name,
          code: item.ref,
          url: parsedUrl,
        };
      case 'merchandise':
        return {
          id: item.id,
          type: item.type,
          title: item.ref || `MER-${item.id}`,
          statusName: typeof item.status_name === 'number' ? item.status_name : undefined,
          code: item.ref,
          url: parsedUrl,
        };
      case 'location':
        return {
          id: item.id,
          type: item.type,
          title: item.code || `LOC-${item.id}`,
          subtitle: item.product_name,
          code: item.code,
          productName: item.product_name,
          quantity: typeof item.qty === 'number' ? item.qty : null,
          url: parsedUrl,
        };
      case 'cart':
        return {
          id: item.id,
          type: item.type,
          title: item.code || `CART-${item.id}`,
          subtitle: Array.isArray(item.preparations)
            ? item.preparations
                .map((prep) => prep.code)
                .filter(Boolean)
                .join(', ')
            : undefined,
          code: item.code,
          url: parsedUrl,
        };
      case 'preparation':
        return {
          id: item.id,
          type: item.type,
          title: item.code || `PREP-${item.id}`,
          subtitle: undefined, // No mostrar subtitle para preparaciones
          // Si status_name es null, usar type_name como estado
          statusName: typeof item.status_name === 'number' 
            ? item.status_name 
            : (typeof item.type_name === 'number' ? item.type_name : undefined),
          code: item.code,
          url: parsedUrl,
        };
      case 'returned_reception':
        return {
          id: item.id,
          type: item.type,
          title: item.reference || `REC-${item.id}`,
          subtitle: undefined, // No mostrar subtitle para recepciones devueltas
          statusName: typeof item.status_name === 'number' ? item.status_name : null,
          code: item.reference,
          url: parsedUrl,
        };
      default:
        return {
          id: item.id,
          type: item.type,
          title: item.name || item.ref || item.code || `Item ${item.id}`,
          subtitle: item.status_name ? String(item.status_name) : undefined,
          code: item.code,
          url: parsedUrl,
        };
    }
  }

  // Obtener path navegable desde la ruta devuelta por la API - Parse path from route
  private getPathFromRoute(route?: string): string | undefined {
    if (!route) return undefined;

    try {
      const url = new URL(route);
      return url.pathname || undefined;
    } catch (_e) {
      return route.startsWith('/') ? route : undefined;
    }
  }
}

// Factory function para crear el adapter
export const createGlobalSearchAdapter = () => {
  const httpClient = createHttpClient(apiClientWH);
  return new GlobalSearchAdapter(httpClient);
};

